import React from "react";
import {BrowserRouter as Router, Route, Switch} from "react-router-dom";

import AuthFailContainer from "../containers/authFail";
import ApplicationSubmit from "../containers/applicationSubmit";
import Onboarding from "../containers/onboarding";
import OnboardingRejected from "../containers/onboardingRejected";
import TranscriptionContainer from "../containers/transcription";
import TranscriptionQualityRatingContainer from "../containers/transcriptQualityRating";
import QaSuperUserContainer from "../containers/qaSuperUser";
import AppContentWrapperContainer from "../containers/appContentWrapper";
import Home from "../containers/home";
import {PageNotFoundFallbackComponent} from "shared/components/wrappers/errorBoundary";
import ScoringPage from "./scoring";
import TqrAlert from "./onboarding/tqrAlert";
import AnnouncementManagerFactory from "shared/components/AnnouncementManager";
import announcements from "./announcements";

const AnnouncementManager =
  AnnouncementManagerFactory(announcements);

/**
 * This is the main entry point of the QA world app.
 * It handles the base routing, including authentication state.
 * @memberof qa-world.components
 * @alias App
 */
export const App = ({currentContractor}) => {
  const PrivateRoute = ({component: Component, ...rest}) => {
    const routeProps = rest?.props ?? {};
    const isOnboardedContractor =
      currentContractor != null && currentContractor.onboardingStep == null;

    if (!isOnboardedContractor) {
      return <AuthFailContainer/>;
    }
    return (
      <Route
        {...rest}
        render={(props) => <Component {...props} {...routeProps} />}
      />
    );
  };

  if (currentContractor?.is_tqr_onboarding) {
    return <TqrAlert/>;
  }

  return (
    <>
      <Router>
        <AppContentWrapperContainer>
          <Switch>
            <Route exact path="/calls" component={Home}/>
            <Route exact path="/onboarding" component={Onboarding}/>
            <Route
              exact
              path="/onboarding/rejected"
              component={OnboardingRejected}
            />
            <Route
              exact
              path="/transcriber_applicants/signup_done"
              component={ApplicationSubmit}
            />
            <PrivateRoute
              exact
              path="/qa/scoring/:callId"
              component={ScoringPage}
            />
            <PrivateRoute
              exact
              path="/qa/false_negatives/:fnCallId"
              component={ScoringPage}
            />
            <PrivateRoute
              exact
              path="/transcriptions/:callId"
              component={TranscriptionContainer}
              props={{
                secondPass: false,
                submitURL: ({display_id}) =>
                  `/transcriptions/${display_id}/new_submit`,
              }}
            />
            <PrivateRoute
              exact
              path="/transcriptions/second_pass/:callId"
              component={TranscriptionContainer}
              props={{
                secondPass: true,
                submitURL: ({display_id}) =>
                  `/transcriptions/second_pass/${display_id}/submit`,
              }}
            />
            <PrivateRoute
              exact
              path="/transcription_qas/perform"
              props={{
                creatingCalibration: false,
                submitURL: ({callDisplayID}) =>
                  `/transcription_qas/${callDisplayID}/submit`,
              }}
              component={TranscriptionQualityRatingContainer}
            />
            <PrivateRoute
              exact
              path="/transcription_qas/:callId/create_calibration_test"
              props={{
                creatingCalibration: true,
                submitURL: ({callDisplayID}) =>
                  `/transcription_qas/${callDisplayID}/create_calibration_test`,
              }}
              component={TranscriptionQualityRatingContainer}
            />
            {currentContractor?.is_super_user && (
              <PrivateRoute
                exact
                path="/qa_super_user"
                component={QaSuperUserContainer}
              />
            )}
            <Route path="/" component={PageNotFoundFallbackComponent}/>
          </Switch>
        </AppContentWrapperContainer>
        <div id="portal-target">
          <AnnouncementManager/>
        </div>
      </Router>
    </>
  );
};

export default React.memo(App);
